/**
 * Migration 003 — One-time IndexedDB → SQLite data copy.
 *
 * When the app upgrades from IndexedDB to SQLite (Capacitor native),
 * synced tables are repopulated via Dexie Cloud. But **unsynced** tables
 * (setting_local, debug_log, import_tmp, doc_cache) and the local
 * privateIndex live only in IndexedDB — they must be copied explicitly.
 *
 * This migration:
 *   1. Detects if the old IndexedDB ("ToToDoDb") has data
 *   2. Copies all unsynced tables from IDB → current database
 *   3. Copies privateIndex (never synced via Dexie Cloud)
 *   4. Marks itself complete so it never runs again
 *
 * Safety:
 *   - Idempotent: skips tables that already have data in the target
 *   - Only runs when `isSqliteBackend()` returns true (Capacitor native)
 *   - Does NOT delete the old IDB — cleanup happens in migration 004
 *
 * The old IndexedDB is opened via raw `indexedDB.open()` (not Dexie)
 * to avoid schema conflicts with the new database instance.
 */

import { db } from "@/db/schema"
import { isSqliteActive } from "@/db/sqliteDetect"
import type { DataMigration } from "./migrationTypes"
import {
  createMigrationLogger,
  type MigrationLogger,
  type TableResult,
} from "./migrationLogger"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** The exact database name used by the old IndexedDB-backed Dexie instance */
const OLD_IDB_NAME = "ToToDoDb"

/**
 * Tables that are never synced via Dexie Cloud.
 * These are the tables at risk of data loss during the switch.
 */
const UNSYNCED_TABLES = [
  "setting_local",
  "debug_log",
  "import_tmp",
  "doc_cache",
] as const

/**
 * Additional tables that hold local-only ordering data.
 * privateIndex stores sidebar order and is NOT synced.
 */
const LOCAL_TABLES = ["privateIndex"] as const

/** All tables to migrate */
const TABLES_TO_MIGRATE = [...UNSYNCED_TABLES, ...LOCAL_TABLES]

const BATCH_SIZE = 200
const YIELD_MS = 30

// ---------------------------------------------------------------------------
// IDB helpers — raw IndexedDB access (no Dexie dependency)
// ---------------------------------------------------------------------------

function openOldIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    // Use raw indexedDB — not the potentially-shimmed version
    const idb =
      typeof indexedDB !== "undefined" ? indexedDB : globalThis.indexedDB
    if (!idb) {
      resolve(null)
      return
    }

    const request = idb.open(OLD_IDB_NAME)

    request.onsuccess = () => {
      const oldDb = request.result
      // Check if it actually has object stores (not just an empty shell)
      if (oldDb.objectStoreNames.length === 0) {
        oldDb.close()
        resolve(null)
        return
      }
      resolve(oldDb)
    }

    request.onerror = () => resolve(null)

    // If the DB doesn't exist, open() will create it with version 1 and no stores.
    // The onupgradeneeded event fires in that case — abort to avoid creating an empty DB.
    request.onupgradeneeded = () => {
      request.transaction?.abort()
      resolve(null)
    }
  })
}

function readAllFromStore<T>(
  idbDb: IDBDatabase,
  storeName: string,
): Promise<T[]> {
  return new Promise((resolve, reject) => {
    if (!idbDb.objectStoreNames.contains(storeName)) {
      resolve([])
      return
    }

    const tx = idbDb.transaction(storeName, "readonly")
    const store = tx.objectStore(storeName)
    const request = store.getAll()

    request.onsuccess = () => resolve(request.result ?? [])
    request.onerror = () => reject(request.error)
  })
}

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

export const idbToSqliteMigration: DataMigration = {
  id: "003-idb-to-sqlite",
  description:
    "One-time copy of unsynced data from IndexedDB to SQLite (Capacitor upgrade)",
  priority: 4, // After userStatus (5) and before doc migration (6) — but this only runs on SQLite

  async needsMigration(): Promise<boolean> {
    // Only relevant when running on SQLite backend (Capacitor native)
    if (!isSqliteActive()) return false

    // Try to open the old IndexedDB
    const oldDb = await openOldIdb()
    if (!oldDb) return false

    // Check if any unsynced table has data
    let hasData = false
    for (const tableName of TABLES_TO_MIGRATE) {
      try {
        const rows = await readAllFromStore(oldDb, tableName)
        if (rows.length > 0) {
          hasData = true
          break
        }
      } catch {
        // Table doesn't exist in old DB — skip
      }
    }

    oldDb.close()
    return hasData
  },

  async execute(): Promise<number> {
    if (!isSqliteActive()) return 0

    const oldDb = await openOldIdb()
    if (!oldDb) return 0

    const logger: MigrationLogger = createMigrationLogger(db.debug_log)
    const migrationStart = performance.now()

    await logger.start("003-idb-to-sqlite", {
      tables: [...TABLES_TO_MIGRATE],
      platform: detectPlatform(),
    })

    let totalMigrated = 0
    let totalSkipped = 0
    let totalErrors = 0
    const tableResults: TableResult[] = []

    try {
      for (const tableName of TABLES_TO_MIGRATE) {
        const tableStart = performance.now()
        try {
          // Check if store exists in old IDB
          if (!oldDb.objectStoreNames.contains(tableName)) {
            totalSkipped++
            const result: TableResult = {
              table: tableName,
              status: "skipped",
              rows: 0,
              durationMs: Math.round(performance.now() - tableStart),
              skipReason: "source-missing",
            }
            tableResults.push(result)
            await logger.tableSkip(tableName, { reason: "source-missing" })
            continue
          }

          const rows = await readAllFromStore(oldDb, tableName)

          if (rows.length === 0) {
            totalSkipped++
            const result: TableResult = {
              table: tableName,
              status: "skipped",
              rows: 0,
              durationMs: Math.round(performance.now() - tableStart),
              skipReason: "source-empty",
            }
            tableResults.push(result)
            await logger.tableSkip(tableName, { reason: "source-empty" })
            continue
          }

          // Get the target table in the new database
          const targetTable = db.table(tableName)

          // Check if target already has data (skip to avoid duplicates)
          const existingCount = await targetTable.count()
          if (existingCount > 0) {
            totalSkipped++
            const result: TableResult = {
              table: tableName,
              status: "skipped",
              rows: 0,
              durationMs: Math.round(performance.now() - tableStart),
              skipReason: "already-has-data",
            }
            tableResults.push(result)
            await logger.tableSkip(tableName, {
              reason: "already-has-data",
              existingRows: existingCount,
            })
            continue
          }

          await logger.tableStart(tableName, { sourceRows: rows.length })

          // Copy in batches
          for (let i = 0; i < rows.length; i += BATCH_SIZE) {
            const batch = rows.slice(i, i + BATCH_SIZE)
            await targetTable.bulkPut(batch)
            totalMigrated += batch.length

            // Yield between batches
            if (i + BATCH_SIZE < rows.length) {
              await new Promise((resolve) => setTimeout(resolve, YIELD_MS))
            }
          }

          const tableDuration = Math.round(performance.now() - tableStart)
          const result: TableResult = {
            table: tableName,
            status: "migrated",
            rows: rows.length,
            durationMs: tableDuration,
          }
          tableResults.push(result)
          await logger.tableEnd(tableName, {
            migratedRows: rows.length,
            durationMs: tableDuration,
          })
        } catch (e) {
          // Log but continue — don't let one table block others
          totalErrors++
          const tableDuration = Math.round(performance.now() - tableStart)
          const result: TableResult = {
            table: tableName,
            status: "error",
            rows: 0,
            durationMs: tableDuration,
            error: e instanceof Error ? e.message : String(e),
          }
          tableResults.push(result)
          await logger.tableError(tableName, e, tableDuration)
        }
      }
    } finally {
      oldDb.close()
    }

    const totalDuration = Math.round(performance.now() - migrationStart)
    await logger.end({
      totalMigrated,
      totalSkipped,
      totalErrors,
      durationMs: totalDuration,
      tableResults,
    })

    return totalMigrated
  },
}

// ---------------------------------------------------------------------------
// Platform detection (best-effort)
// ---------------------------------------------------------------------------

function detectPlatform(): string {
  try {
    const ua = navigator.userAgent ?? ""
    if (/iPad|iPhone|iPod/.test(ua)) return "ios"
    if (/Android/.test(ua)) return "android"
    return "web"
  } catch {
    return "unknown"
  }
}
